/* eslint-disable react/no-array-index-key */
import React, { useContext, useEffect, useState } from 'react';
import { Loader } from '@saviynt/design-system';

import { RequestStatus, type Response, ResponseFailure } from '../../../models';
import {
  baseUrl,
  isEnumMember,
  P,
  useInvertedLocalize,
} from '../../../utilities';
import { type AvailableFactor, type FactorType } from '../models';
import { type RootState, useDispatch, useSelector } from '../store';
import { thunks } from '../store';

import FactorCard from './FactorCard';
import { invertedMessages } from './messages';
import MfaError from './MfaError';
import WindowContext from './WindowContext';

import './Menu.css';

interface MenuProps {
  /** Called when the user selects a factor */
  onUserSelect: (factor: FactorType) => void;

  /** Called when only one factor is available */
  onAutoSelect: (factor: FactorType) => void;
}

interface SelectedState {
  availableFactors: Response<AvailableFactor[]>;
  availableFactorsStatus: RequestStatus;
}

/** Retrieves and displays the available factors for the user to select */
export default function Menu({
  onUserSelect: handleSelect,
  onAutoSelect,
}: MenuProps): React.JSX.Element | null {
  const { availableFactors, availableFactorsStatus } = useSelector(selectState);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const dispatch = useDispatch();
  const l = useInvertedLocalize(invertedMessages);
  const isError = isEnumMember(availableFactors, ResponseFailure);
  const automaticSelection =
    !isError && availableFactors?.length === 1 ? availableFactors[0] : null;
  const { locationAssign } = useContext(WindowContext);

  useLoadAvailableFactorsEffect(
    availableFactors,
    availableFactorsStatus,
    setIsRedirecting,
    locationAssign
  );
  useEffect(() => {
    if (automaticSelection) {
      onAutoSelect(automaticSelection.type);
    }
  }, [automaticSelection, onAutoSelect]);

  if (
    availableFactors === undefined ||
    availableFactorsStatus === RequestStatus.Pending ||
    isRedirecting
  ) {
    return (
      <div className='Nav_Menu Nav_Menu--isLoading'>
        <Loader kind='loop' format='main' color='brand' />
        <P>{l('Loading your authentication methods...')}</P>
      </div>
    );
  }

  if (isError) {
    const handleRetry = () => dispatch(thunks.requestAvailableFactors());

    return <MfaError onRetry={handleRetry} />;
  }

  if (automaticSelection) {
    return null;
  }

  return (
    <div className='Nav_Menu Nav_Menu--isOptions'>
      {availableFactors.map(({ type }) => (
        <FactorCard key={type} item={type} onSelect={handleSelect} />
      ))}
    </div>
  );
}

/**
 * Dispatches the available factor retrieval thunk if the data is not already
 * loaded
 */
function useLoadAvailableFactorsEffect(
  availableFactors: Response<AvailableFactor[]>,
  availableFactorsStatus: RequestStatus,
  setIsRedirecting: (value: boolean) => void,
  locationAssign: (url: string) => void
) {
  const dispatch = useDispatch();

  useEffect(() => {
    if (availableFactors === undefined) {
      dispatch(thunks.requestAvailableFactors());

      return;
    }

    if (isEnumMember(availableFactors, ResponseFailure)) {
      return;
    }

    const totp = availableFactors.find(({ type }) => type === 'totp');

    if (totp?.isRegistered === false) {
      const url =
        window.location.pathname +
        window.location.search +
        window.location.hash;

      locationAssign(
        `${baseUrl}/ECMv6/auth/mfa-setup?url=${encodeURIComponent(url)}`
      );
      setIsRedirecting(true);
    }
  }, [
    availableFactorsStatus,
    availableFactors,
    dispatch,
    locationAssign,
    setIsRedirecting,
  ]);
}

function selectState({
  mfaPrompt: { availableFactors, availableFactorsStatus },
}: RootState): SelectedState {
  return {
    availableFactors,
    availableFactorsStatus,
  };
}
